import { useCallback, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getProject, updateProject } from '../api/client';
import Spinner from '../components/ui/Spinner';
import EditorDockLayout from '../components/editor/EditorDockLayout';
import TopBar from '../components/editor/TopBar';
import NodeEditDialog from '../components/nodeConfig/NodeEditDialog';
import BrandLogo from '../components/BrandLogo';
import { useEditorStore } from '../store/editorStore';

// ------------------------------------------------------------
// Page: Project editor shell
// ------------------------------------------------------------

export default function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const record = useEditorStore((s) => s.record);
  const document = useEditorStore((s) => s.document);
  const isDirty = useEditorStore((s) => s.isDirty);
  const isSaving = useEditorStore((s) => s.isSaving);
  const isLoading = useEditorStore((s) => s.isLoading);
  const error = useEditorStore((s) => s.error);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const selectedRouteIds = useEditorStore((s) => s.selectedRouteIds);
  const routeSourceId = useEditorStore((s) => s.routeSourceId);
  const calibrationMode = useEditorStore((s) => s.calibrationMode);
  const permission = useEditorStore((s) => s.permission);
  const loadProject = useEditorStore((s) => s.loadProject);
  const setLoading = useEditorStore((s) => s.setLoading);
  const setError = useEditorStore((s) => s.setError);
  const saveProject = useEditorStore((s) => s.saveProject);
  const reset = useEditorStore((s) => s.reset);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);
  const copySelectedNodes = useEditorStore((s) => s.copySelectedNodes);
  const pasteNodes = useEditorStore((s) => s.pasteNodes);
  const selectNode = useEditorStore((s) => s.selectNode);
  const setNodeSelection = useEditorStore((s) => s.setNodeSelection);
  const setRouteSource = useEditorStore((s) => s.setRouteSource);
  const cancelCalibration = useEditorStore((s) => s.cancelCalibration);
  const closeNodeEdit = useEditorStore((s) => s.closeNodeEdit);
  const openNodeEdit = useEditorStore((s) => s.openNodeEdit);
  const focusedGroupId = useEditorStore((s) => s.focusedGroupId);
  const exitGroupFocus = useEditorStore((s) => s.exitGroupFocus);
  const editingNodeId = useEditorStore((s) => s.editingNodeId);
  const patchRecordMeta = useEditorStore((s) => s.patchRecordMeta);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getProject(id);
        if (!cancelled) loadProject(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load project');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
      reset();
    };
  }, [id, loadProject, reset, setError, setLoading]);

  const readOnly = permission !== 'edit';

  const handleCloseProject = useCallback(() => {
    if (isDirty) {
      const leave = window.confirm(
        'You have unsaved changes. Leave anyway?',
      );
      if (!leave) return;
    }
    navigate('/');
  }, [isDirty, navigate]);

  const handleRenameProject = useCallback(
    async (name: string) => {
      if (readOnly || !id) return;
      setError(null);
      try {
        const updated = await updateProject(id, { name });
        patchRecordMeta({ name: updated.name });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to rename project');
        throw err;
      }
    },
    [id, patchRecordMeta, readOnly, setError],
  );

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return (
        el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.isContentEditable
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && !isSaving && !readOnly) void saveProject();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && !e.altKey) {
        if (e.key === 'z' || e.key === 'Z') {
          if (isTypingTarget(e.target)) return;
          e.preventDefault();
          if (!readOnly) {
            if (e.shiftKey) redo();
            else undo();
          }
          return;
        }
        if (e.key === 'y' || e.key === 'Y') {
          if (isTypingTarget(e.target)) return;
          e.preventDefault();
          if (!readOnly) redo();
          return;
        }
      }
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        handleCloseProject();
        return;
      }
      if (e.key === 'Escape') {
        if (isTypingTarget(e.target)) return;

        const hasSelectionState =
          focusedGroupId != null ||
          selectedNodeIds.length > 0 ||
          selectedRouteIds.length > 0 ||
          routeSourceId != null ||
          calibrationMode ||
          editingNodeId != null;

        if (!hasSelectionState) return;

        e.preventDefault();
        if (focusedGroupId) {
          exitGroupFocus();
          return;
        }
        if (editingNodeId) closeNodeEdit();
        if (routeSourceId) setRouteSource(null);
        if (calibrationMode) cancelCalibration();
        selectNode(null);
        return;
      }
      if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (isTypingTarget(e.target)) return;
        if (editingNodeId || selectedNodeIds.length === 0) return;
        e.preventDefault();
        openNodeEdit(selectedNodeIds[0]);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        if (isTypingTarget(e.target)) return;
        const nodes = document?.nodes;
        if (!nodes?.length) return;
        e.preventDefault();
        if (routeSourceId) setRouteSource(null);
        setNodeSelection(
          nodes.map((n) => n.id),
          { focus: true },
        );
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        if (isTypingTarget(e.target)) {
          return;
        }
        if (selectedNodeIds.length > 0) {
          e.preventDefault();
          copySelectedNodes();
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        if (isTypingTarget(e.target)) {
          return;
        }
        if (!readOnly) {
          e.preventDefault();
          pasteNodes();
        }
        return;
      }
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        (selectedNodeIds.length > 0 || selectedRouteIds.length > 0)
      ) {
        if (isTypingTarget(e.target)) {
          return;
        }
        e.preventDefault();
        if (!readOnly) deleteSelected();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    saveProject,
    handleCloseProject,
    isDirty,
    isSaving,
    readOnly,
    document,
    selectedNodeIds,
    selectedRouteIds,
    routeSourceId,
    calibrationMode,
    editingNodeId,
    focusedGroupId,
    exitGroupFocus,
    deleteSelected,
    copySelectedNodes,
    pasteNodes,
    selectNode,
    setNodeSelection,
    setRouteSource,
    cancelCalibration,
    closeNodeEdit,
    openNodeEdit,
    undo,
    redo,
  ]);

  if (!id) {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-6 bg-surface">
        <BrandLogo variant="mark" />
        <Spinner label="Loading project?" />
      </div>
    );
  }

  if (error && !record) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-surface px-6 text-center">
        <div className="alert-danger px-6 py-4">
          <p>{error}</p>
        </div>
        <Link to="/" className="btn-secondary">
          Back to library
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-surface">
      <TopBar
        onSave={() => void saveProject()}
        onCloseProject={handleCloseProject}
        onRenameProject={handleRenameProject}
        isDirty={isDirty}
        isSaving={isSaving}
        error={error}
        readOnly={readOnly}
      />
      <div className="flex min-h-0 flex-1">
        <EditorDockLayout />
      </div>
      <NodeEditDialog />
    </div>
  );
}
